import { FormEvent, useEffect, useState } from 'react';
import type { Certificado, Configuracion, EmisorInput, FacturacionApi, RucConsulta } from '../api/types';
import { diasParaVencer, esCertificado, fecha, LIMITES, LOGO_MIME } from '../validacion';
import { Alerta, Boton, Campo, ErrorApi, Guia, Pill } from './ui';

interface Props {
  api: FacturacionApi;
  config: Configuracion;
  onGuardado: (c: Configuracion) => void;
}

type Errores = Partial<Record<keyof EmisorInput, string>>;

export function EmisorSeccion({ api, config, onGuardado }: Props) {
  const e = config.emisor;
  const registrado = !!e?.alanubeIdCompany;

  const [d, setD] = useState<EmisorInput>(() => inicial(config));
  const [errores, setErrores] = useState<Errores>({});
  const [guardando, setGuardando] = useState(false);
  const [errorApi, setErrorApi] = useState<unknown>(null);
  const [exito, setExito] = useState<'registrado' | 'guardado' | null>(null);

  const [consultando, setConsultando] = useState(false);
  const [rucInfo, setRucInfo] = useState<RucConsulta | null>(null);
  const [rucError, setRucError] = useState<unknown>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  useEffect(() => {
    if (!d.logo) return setLogoPreview(null);
    const url = URL.createObjectURL(d.logo);
    setLogoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [d.logo]);

  const set = <K extends keyof EmisorInput>(k: K, v: EmisorInput[K]) => {
    setD((x) => ({ ...x, [k]: v }));
    setErrores((x) => ({ ...x, [k]: undefined }));
    setExito(null);
  };

  async function consultarRuc() {
    const ruc = d.ruc.trim();
    if (ruc.length < LIMITES.rucMin) {
      setErrores((x) => ({ ...x, ruc: `Ingrese al menos ${LIMITES.rucMin} caracteres.` }));
      return;
    }
    setConsultando(true);
    setRucError(null);
    setRucInfo(null);
    try {
      const r = await api.consultarRuc(config.idEmpresa, ruc, d.tipoRuc);
      setRucInfo(r);
      if (r.exito) setD((x) => ({ ...x, razonSocial: r.nombre ?? x.razonSocial, dv: r.dv ?? x.dv }));
    } catch (err) {
      setRucError(err);
    } finally {
      setConsultando(false);
    }
  }

  function validar(): Errores {
    const er: Errores = {};
    const ruc = d.ruc.trim();
    if (!ruc) er.ruc = 'El RUC es obligatorio.';
    else if (ruc.length < LIMITES.rucMin || ruc.length > LIMITES.rucMax)
      er.ruc = `Entre ${LIMITES.rucMin} y ${LIMITES.rucMax} caracteres.`;
    if (!registrado && !d.razonSocial) er.ruc = er.ruc ?? 'Consulte el RUC en la DGI para confirmar el nombre y dígito verificador.';

    const nc = d.nombreComercial.trim();
    if (nc && (nc.length < LIMITES.nombreComercialMin || nc.length > LIMITES.nombreComercialMax))
      er.nombreComercial = `Entre ${LIMITES.nombreComercialMin} y ${LIMITES.nombreComercialMax} caracteres.`;

    if (d.logo) {
      if (!LOGO_MIME.includes(d.logo.type)) er.logo = 'Sólo JPEG, PNG o WebP (SVG no es aceptado).';
      else if (d.logo.size > LIMITES.logoBytes) er.logo = 'Máximo 150 KB.';
    }

    for (const [archivo, clave, kArch, kClave] of [
      [d.certificadoFirma, d.certificadoFirmaPassword, 'certificadoFirma', 'certificadoFirmaPassword'],
      [d.certificadoAutenticacion, d.certificadoAutenticacionPassword, 'certificadoAutenticacion', 'certificadoAutenticacionPassword'],
    ] as const) {
      if (!registrado && !archivo) er[kArch] = 'Obligatorio para registrar el comercio.';
      if (archivo && !esCertificado(archivo)) er[kArch] = 'Debe ser un archivo .p12 o .pfx.';
      if (archivo && !clave) er[kClave] = 'Indique la contraseña del certificado.';
    }

    return er;
  }

  async function guardar(ev: FormEvent) {
    ev.preventDefault();
    const er = validar();
    setErrores(er);
    if (Object.values(er).some(Boolean)) return;
    setGuardando(true);
    setErrorApi(null);
    try {
      const eraNuevo = !registrado;
      const c = await api.guardarEmisor(config.idEmpresa, d);
      onGuardado(c);
      // Los archivos y contraseñas no se conservan en el navegador después de enviarlos.
      setD((x) => ({
        ...x,
        certificadoFirma: null,
        certificadoFirmaPassword: '',
        certificadoAutenticacion: null,
        certificadoAutenticacionPassword: '',
        logo: null,
      }));
      setExito(eraNuevo ? 'registrado' : 'guardado');
    } catch (err) {
      setErrorApi(err);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <form className="seccion" onSubmit={guardar} noValidate>
      <Guia
        id="emisor"
        titulo="Cómo registrar su comercio"
        pasos={[
          <>Sus comprobantes se emiten a través de <b>Alanube</b>, Proveedor de Autorización Calificado (PAC) autorizado por la DGI.</>,
          <>Tenga a mano su <b>RUC</b> y los <b>dos certificados digitales .p12</b> de la DGI (firma y autenticación) con sus contraseñas.</>,
          <>Elija el tipo de contribuyente, escriba el RUC y pulse <b>Consultar DGI</b>: se completan la razón social y el dígito verificador.</>,
          <>Cargue los dos certificados y escriba la contraseña de cada uno.</>,
          <>Opcional: nombre comercial (si debe imprimirse distinto al registrado en la DGI) y logo.</>,
          <>Pulse <b>Registrar comercio</b>. La solicitud de afiliación a la DGI se envía sola. Luego continúe con <b>Sucursales</b>.</>,
        ]}
        nota="Cuando un certificado esté por vencer, cargue el nuevo aquí y pulse Guardar cambios. El RUC no se puede cambiar después del registro."
      />
      <header className="seccion__cab">
        <div>
          <h2>Datos del emisor</h2>
          <p className="texto-sec">
            Información del contribuyente ante la DGI. Al guardar por primera vez, el comercio queda registrado para emitir y se
            envía automáticamente la solicitud de afiliación a la DGI.
          </p>
        </div>
        {registrado ? <Pill tono="ok">Registrado</Pill> : <Pill tono="pendiente">Sin registrar</Pill>}
      </header>

      {/* ---------------- Contribuyente ---------------- */}
      <fieldset className="bloque">
        <legend>Contribuyente</legend>

        <div className="grid-2">
          <Campo etiqueta="Tipo de contribuyente" ayuda={registrado ? 'No se puede cambiar después del registro.' : undefined}>
            {() => (
              <div className="segmentado" role="radiogroup" aria-label="Tipo de contribuyente">
                {([2, 1] as const).map((t) => (
                  <label key={t} className={d.tipoRuc === t ? 'activo' : ''}>
                    <input
                      type="radio"
                      name="tipoRuc"
                      checked={d.tipoRuc === t}
                      disabled={registrado}
                      onChange={() => {
                        set('tipoRuc', t);
                        setRucInfo(null);
                      }}
                    />
                    {t === 2 ? 'Persona jurídica' : 'Persona natural'}
                  </label>
                ))}
              </div>
            )}
          </Campo>

          <Campo
            etiqueta="RUC"
            error={errores.ruc}
            ayuda={registrado ? 'No se puede cambiar después del registro.' : 'Consulte en la DGI para obtener nombre y DV.'}
          >
            {(id) => (
              <div className="input-con-boton">
                <input
                  id={id}
                  value={d.ruc}
                  maxLength={LIMITES.rucMax}
                  disabled={registrado}
                  autoComplete="off"
                  onChange={(ev) => {
                    const v = ev.target.value;
                    setD((x) => ({ ...x, ruc: v, razonSocial: '', dv: '' }));
                    setErrores((x) => ({ ...x, ruc: undefined }));
                    setRucInfo(null);
                    setExito(null);
                  }}
                />
                {!registrado && (
                  <Boton tipo="secundario" onClick={consultarRuc} cargando={consultando}>
                    Consultar DGI
                  </Boton>
                )}
              </div>
            )}
          </Campo>
        </div>

        {rucError ? <ErrorApi error={rucError} /> : null}
        {rucInfo && !rucInfo.exito && (
          <Alerta tipo="aviso" titulo="La DGI no confirmó el RUC">
            {rucInfo.codigoDgi && `[${rucInfo.codigoDgi}] `}
            {rucInfo.mensajeDgi}
          </Alerta>
        )}
        {rucInfo?.exito && (
          <Alerta tipo="info" titulo="RUC encontrado en la DGI">
            Afiliado al SFE: <b>{siNo(rucInfo.afiliadoSfe)}</b> · Resolución de autorización: <b>{siNo(rucInfo.autorizado)}</b>
          </Alerta>
        )}

        <div className="grid-3">
          <Campo etiqueta="Razón social (DGI)">
            {(id) => <input id={id} value={d.razonSocial} readOnly placeholder="Se completa al consultar el RUC" />}
          </Campo>
          <Campo etiqueta="DV">{(id) => <input id={id} value={d.dv} readOnly className="corto" />}</Campo>
          <Campo
            etiqueta="Nombre comercial"
            opcional
            error={errores.nombreComercial}
            ayuda="Sólo si debe imprimirse un nombre distinto al registrado en la DGI."
            contador={{ actual: d.nombreComercial.length, max: LIMITES.nombreComercialMax }}
          >
            {(id) => (
              <input id={id} value={d.nombreComercial} onChange={(ev) => set('nombreComercial', ev.target.value)} />
            )}
          </Campo>
        </div>

        <Campo
          etiqueta="Logo para el comprobante"
          opcional
          error={errores.logo}
          ayuda={e?.tieneLogo && !d.logo ? 'Ya hay un logo cargado. Seleccione otro sólo si desea reemplazarlo.' : 'JPEG, PNG o WebP · máximo 150 KB.'}
        >
          {(id) => (
            <div className="archivo">
              <input id={id} type="file" accept={LOGO_MIME.join(',')} onChange={(ev) => set('logo', ev.target.files?.[0] ?? null)} />
              {logoPreview && <img src={logoPreview} alt="Vista previa del logo" className="logo-preview" />}
            </div>
          )}
        </Campo>
      </fieldset>

      {/* ---------------- Certificados ---------------- */}
      <fieldset className="bloque">
        <legend>Certificados digitales de la DGI</legend>
        <p className="texto-sec">
          Los dos certificados .p12 que emite la DGI. Se envían por conexión segura al PAC, que los custodia; el archivo y la
          contraseña no se guardan en este portal.
          {registrado && ' Cargue un archivo sólo si desea reemplazar el actual.'}
        </p>
        <div className="grid-2">
          <CertificadoCampo
            titulo="Certificado de firma"
            actual={e?.certificadoFirma ?? null}
            archivo={d.certificadoFirma}
            clave={d.certificadoFirmaPassword}
            errorArchivo={errores.certificadoFirma}
            errorClave={errores.certificadoFirmaPassword}
            onArchivo={(f) => set('certificadoFirma', f)}
            onClave={(v) => set('certificadoFirmaPassword', v)}
          />
          <CertificadoCampo
            titulo="Certificado de autenticación"
            actual={e?.certificadoAutenticacion ?? null}
            archivo={d.certificadoAutenticacion}
            clave={d.certificadoAutenticacionPassword}
            errorArchivo={errores.certificadoAutenticacion}
            errorClave={errores.certificadoAutenticacionPassword}
            onArchivo={(f) => set('certificadoAutenticacion', f)}
            onClave={(v) => set('certificadoAutenticacionPassword', v)}
          />
        </div>
      </fieldset>

      <ErrorApi error={errorApi} />
      {exito === 'registrado' && (
        <Alerta tipo="exito" titulo="Comercio registrado.">
          Se envió la solicitud de afiliación a la DGI. Continúe con la sucursal principal.
        </Alerta>
      )}
      {exito === 'guardado' && <Alerta tipo="exito" titulo="Cambios guardados." />}

      <div className="acciones">
        <Boton submit cargando={guardando}>
          {registrado ? 'Guardar cambios' : 'Registrar comercio'}
        </Boton>
      </div>
    </form>
  );
}

function CertificadoCampo(p: {
  titulo: string;
  actual: Certificado | null;
  archivo: File | null;
  clave: string;
  errorArchivo?: string;
  errorClave?: string;
  onArchivo: (f: File | null) => void;
  onClave: (v: string) => void;
}) {
  const dias = diasParaVencer(p.actual?.hasta ?? null);
  return (
    <div className="tarjeta-cert">
      <div className="tarjeta-cert__cab">
        <h3>{p.titulo}</h3>
        {p.actual?.hasta ? (
          dias !== null && dias < 0 ? (
            <Pill tono="error">Vencido</Pill>
          ) : dias !== null && dias <= 30 ? (
            <Pill tono="pendiente">Vence en {dias} días</Pill>
          ) : (
            <Pill tono="ok">Vigente</Pill>
          )
        ) : (
          <Pill tono="neutro">Sin cargar</Pill>
        )}
      </div>
      {p.actual?.hasta && (
        <dl className="meta">
          <dt>Emitido por</dt>
          <dd>{p.actual.emisor ?? '—'}</dd>
          <dt>Vigencia</dt>
          <dd>
            {fecha(p.actual.desde)} – {fecha(p.actual.hasta)}
          </dd>
        </dl>
      )}
      <Campo etiqueta="Archivo .p12" error={p.errorArchivo}>
        {(id) => <input id={id} type="file" accept=".p12,.pfx" onChange={(ev) => p.onArchivo(ev.target.files?.[0] ?? null)} />}
      </Campo>
      {p.archivo && (
        <Campo etiqueta="Contraseña del certificado" error={p.errorClave}>
          {(id) => (
            <input id={id} type="password" autoComplete="new-password" value={p.clave} onChange={(ev) => p.onClave(ev.target.value)} />
          )}
        </Campo>
      )}
    </div>
  );
}

function inicial(c: Configuracion): EmisorInput {
  const e = c.emisor;
  return {
    tipoRuc: e?.tipoRuc ?? 2,
    ruc: e?.ruc ?? '',
    dv: e?.dv ?? '',
    razonSocial: e?.razonSocial ?? '',
    nombreComercial: e?.nombreComercial ?? '',
    emailActivo: false, // el correo al cliente lo envía el sistema; no se usa el del PAC
    emailReplyTo: e?.emailReplyTo ?? '',
    emailMensaje: e?.emailMensaje ?? '',
    certificadoFirma: null,
    certificadoFirmaPassword: '',
    certificadoAutenticacion: null,
    certificadoAutenticacionPassword: '',
    logo: null,
  };
}

const siNo = (v: boolean | null) => (v === null ? '—' : v ? 'Sí' : 'No');
